import { apiFetch } from "../api/client";
import type { EventContext } from "../context/event";
import type { Scope } from "./types";

/**
 * TRANSLATING A SETTING, with Claude Sonnet — `POST /gpt/translate/settings-fo`.
 *
 * ⚠️ THE ROUTE WRITES TO THE DATABASE ITSELF. It asks the model, then saves each
 * translation through `updateFo` before answering. Two consequences that decide
 * how this is used:
 *
 *   — Nothing goes through the auto-save queue. What comes back is already
 *     stored; the client's job is to catch up, not to send it again.
 *   — It writes to every target language at once. So it is only offered while
 *     reading the REFERENCE language: from any other, the source would be a
 *     translation and the reference would be among the things overwritten.
 *
 * ⚠️ THE TARGETS ARE NAMED BY US. Left to itself the route reads
 * `EVENT_<id>_LANGUAGES_SHOW_V2` — V1's list — and translated into English alone
 * however many languages V2 had turned on, because English is all V1 declares on
 * this event. V2 keeps its own list in its own namespace, so it has to say which
 * languages it means.
 */

export type Translation = { languageId: number; value: string };

/**
 * Where the value lives, in the shape the route expects.
 *
 * A plain key for a shared value; an array path for a typology override — the
 * route's `setDeepValue` walks it. The same two cases as `writePath` and
 * `clearPath`, and for the same reason: what one writes, the others must reach.
 */
export function translationName(scope: Scope, key: string): string | string[] {
  return scope.typoId ? ["typosV2", String(scope.typoId), key] : key;
}

export async function translateSetting(
  event: EventContext,
  scope: Scope,
  key: string,
  source: string,
  /** Language ids to fill — every active one except the reference. */
  targets: ReadonlyArray<number>,
  signal?: AbortSignal,
): Promise<Translation[]> {
  const answer = await apiFetch<{ translations?: Translation[] }>(
    "/gpt/translate/settings-fo",
    {
      api: "apix",
      method: "POST",
      body: {
        eventId: event.eventId,
        domainId: event.domainId,
        name: translationName(scope, key),
        source,
        targets,
      },
      signal,
    },
  );

  return clean(answer?.translations);
}

/**
 * TRANSLATING THE WHOLE SCREEN IN ONE CALL.
 *
 * The same route, given `settings` instead of `name`/`source`. It used to be
 * walked one setting at a time — thirty settings meant thirty HTTP round trips,
 * thirty model calls, and thirty read-merge-write cycles per language. At the
 * hundred settings this panel is heading for, that is minutes of waiting for
 * work the route can batch.
 *
 * ⚠️ THE ANSWER IS ALIGNED ON WHAT WAS SENT, index by index. A setting the route
 * could not translate comes back with an empty list rather than being dropped,
 * so a partial failure stays readable: what is missing is still missing, and the
 * count says so.
 */
export async function translateSettings(
  event: EventContext,
  scope: Scope,
  keys: ReadonlyArray<{ key: string; source: string }>,
  targets: ReadonlyArray<number>,
  signal?: AbortSignal,
): Promise<Translation[][]> {
  if (keys.length === 0) return [];

  const answer = await apiFetch<{
    results?: Array<{ translations?: Translation[] }>;
  }>("/gpt/translate/settings-fo", {
    api: "apix",
    method: "POST",
    body: {
      eventId: event.eventId,
      domainId: event.domainId,
      settings: keys.map(({ key, source }) => ({
        name: translationName(scope, key),
        source,
      })),
      targets,
    },
    signal,
  });

  return keys.map((_, i) => clean(answer?.results?.[i]?.translations));
}

// A model can answer anything; what reaches the rows must be a language id and
// a string, or the panel starts rendering objects.
function clean(translations: Translation[] | undefined): Translation[] {
  return (translations ?? []).filter(
    (t): t is Translation =>
      typeof t?.languageId === "number" && typeof t?.value === "string",
  );
}
